import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'

// Which news items this member has already read. Kept per user because a shared
// device (a gym tablet, a family phone) must not hand one member's read state to
// the next.
//
// Events used to be tracked here too. They are not any more: an event's bullet
// is cleared by the event happening, not by the member having glanced at it, so
// there was nothing left for a seen record to decide.
const NEWS_KEY_PREFIX = 'structural:seen-news:v1:'

// Ids are stored with the timestamp they were seen at, so the record can be
// pruned by age. Dropping ids that are simply absent from the current feed would
// be wrong: a multi-gym member switching gyms would lose the other gym's state.
type SeenRecord = Record<string, number>

const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000
const MAX_ENTRIES = 500

function read(key: string | null): SeenRecord {
  if (!key) return {}
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed as SeenRecord
  } catch {
    // Unreadable or corrupt: start over rather than break the page.
    return {}
  }
}

function write(key: string | null, value: SeenRecord): void {
  if (!key) return
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Storage is full or blocked (Safari private mode). The badge just stops
    // remembering, which is a far better outcome than a crash.
  }
}

// Keeps the record bounded: forget anything stale, then cap what remains to the
// most recently seen ids.
function prune(record: SeenRecord, now: number): SeenRecord {
  const fresh = Object.entries(record).filter(([, seenAt]) => now - seenAt <= MAX_AGE_MS)
  if (fresh.length <= MAX_ENTRIES) return Object.fromEntries(fresh)
  fresh.sort((a, b) => b[1] - a[1])
  return Object.fromEntries(fresh.slice(0, MAX_ENTRIES))
}

interface SeenNewsValue {
  seenNews: ReadonlySet<string>
  markNewsSeen: (ids: string[]) => void
}

// The default is inert on purpose: components rendered outside the provider (in
// tests, or before a user is known) still work, they just never remember.
const SeenNewsContext = createContext<SeenNewsValue>({
  seenNews: new Set(),
  markNewsSeen: () => {},
})

export function SeenNewsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const userId = user?.sub ?? null
  const newsKey = userId ? `${NEWS_KEY_PREFIX}${userId}` : null

  const [news, setNews] = useState<SeenRecord>(() => read(newsKey))

  // Reload when the signed-in user changes, so switching accounts never shows
  // the previous member's read state.
  useEffect(() => {
    setNews(read(newsKey))
  }, [newsKey])

  // Returns the previous record unchanged when nothing is new. Pages call this
  // from an effect on every render pass, so a no-op has to stay a no-op or the
  // state update would loop.
  const markNewsSeen = useCallback(
    (ids: string[]) => {
      if (!ids.length) return
      setNews((prev) => {
        const fresh = ids.filter((id) => !prev[id])
        if (!fresh.length) return prev
        const now = Date.now()
        const next = prune({ ...prev, ...Object.fromEntries(fresh.map((id) => [id, now])) }, now)
        write(newsKey, next)
        return next
      })
    },
    [newsKey],
  )

  const value = useMemo<SeenNewsValue>(
    () => ({ seenNews: new Set(Object.keys(news)), markNewsSeen }),
    [news, markNewsSeen],
  )

  return <SeenNewsContext.Provider value={value}>{children}</SeenNewsContext.Provider>
}

export function useSeenNews(): SeenNewsValue {
  return useContext(SeenNewsContext)
}
